import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { useI18n } from '../i18n/index.tsx';
import { api } from '../api/client.ts';
import { useApiError } from '../api/describe.ts';
import {
  Button,
  Card,
  CardHeader,
  Checkbox,
  ChoiceGroup,
  Field,
  Notice,
  TextArea,
  TextInput,
} from '../components/ui.tsx';

/**
 * Adding a server.
 *
 * The screen where a customer hands over access to a machine, so it says in
 * plain words what will happen next: the details are stored encrypted, we look
 * the machine over, and nothing is installed until they have seen the plan.
 * The authorisation checkbox is not a formality — the request is refused
 * without it.
 */
export function AddServerForm({
  onAdded,
  onCancel,
}: {
  onAdded: () => void | Promise<void>;
  onCancel: () => void;
}): ReactNode {
  const { t } = useI18n();
  const describe = useApiError();
  const [name, setName] = useState('');
  const [host, setHost] = useState('');
  const [sshPort, setSshPort] = useState('22');
  const [username, setUsername] = useState('root');
  const [locationLabel, setLocationLabel] = useState('');
  const [method, setMethod] = useState<'key' | 'password'>('key');
  const [privateKey, setPrivateKey] = useState('');
  const [privateKeyPassphrase, setPrivateKeyPassphrase] = useState('');
  const [password, setPassword] = useState('');
  const [sudoPassword, setSudoPassword] = useState('');
  const [authorized, setAuthorized] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (!authorized) return;
    setError(undefined);
    setBusy(true);
    try {
      await api.addServer({
        name: name.trim(),
        host: host.trim(),
        sshPort: Number(sshPort) || 22,
        username: username.trim(),
        locationLabel: locationLabel.trim() || undefined,
        authorizationConfirmed: true,
        ...(method === 'key'
          ? {
              privateKey: privateKey.trim(),
              privateKeyPassphrase: privateKeyPassphrase || undefined,
            }
          : { password }),
        sudoPassword: sudoPassword || undefined,
      });
      await onAdded();
    } catch (caught) {
      setError(describe(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader title={t('servers.add')} description={t('dashboard.noServersHelp')} />

      <form className="form-grid" onSubmit={(event) => void submit(event)}>
        <Field label={t('servers.name')} required>
          {(props) => (
            <TextInput
              {...props}
              value={name}
              required
              maxLength={80}
              placeholder={t('servers.namePlaceholder')}
              onChange={(event) => setName(event.target.value)}
            />
          )}
        </Field>

        <Field label={t('servers.location')}>
          {(props) => (
            <TextInput
              {...props}
              value={locationLabel}
              maxLength={80}
              placeholder={t('servers.locationPlaceholder')}
              onChange={(event) => setLocationLabel(event.target.value)}
            />
          )}
        </Field>

        <Field label={t('servers.host')} help={t('servers.hostHelp')} required>
          {(props) => (
            <TextInput
              {...props}
              className="ltr-text"
              value={host}
              required
              autoComplete="off"
              spellCheck={false}
              onChange={(event) => setHost(event.target.value)}
            />
          )}
        </Field>

        <div className="form-row">
          <Field label={t('servers.port')} required>
            {(props) => (
              <TextInput
                {...props}
                className="ltr-text"
                type="number"
                min={1}
                max={65535}
                value={sshPort}
                required
                onChange={(event) => setSshPort(event.target.value)}
              />
            )}
          </Field>
          <Field label={t('servers.username')} help={t('servers.usernameHelp')} required>
            {(props) => (
              <TextInput
                {...props}
                className="ltr-text"
                value={username}
                required
                autoComplete="off"
                spellCheck={false}
                onChange={(event) => setUsername(event.target.value)}
              />
            )}
          </Field>
        </div>

        <ChoiceGroup
          legend={t('servers.accessMethod')}
          value={method}
          onChange={setMethod}
          options={[
            { value: 'key', title: t('servers.accessKey'), recommended: true },
            { value: 'password', title: t('servers.accessPassword') },
          ]}
        />

        {method === 'key' ? (
          <>
            <Field label={t('servers.privateKey')} help={t('servers.privateKeyHelp')} required>
              {(props) => (
                <TextArea
                  {...props}
                  value={privateKey}
                  required
                  spellCheck={false}
                  rows={8}
                  placeholder={'-----BEGIN OPENSSH PRIVATE KEY-----'}
                  onChange={(event) => setPrivateKey(event.target.value)}
                />
              )}
            </Field>
            <Field label={t('servers.privateKeyPassphrase')}>
              {(props) => (
                <TextInput
                  {...props}
                  type="password"
                  className="ltr-text"
                  value={privateKeyPassphrase}
                  autoComplete="off"
                  onChange={(event) => setPrivateKeyPassphrase(event.target.value)}
                />
              )}
            </Field>
          </>
        ) : (
          <Field label={t('servers.passwordLabel')} required>
            {(props) => (
              <TextInput
                {...props}
                type="password"
                className="ltr-text"
                value={password}
                required
                autoComplete="off"
                onChange={(event) => setPassword(event.target.value)}
              />
            )}
          </Field>
        )}

        <Field label={t('servers.sudoPassword')}>
          {(props) => (
            <TextInput
              {...props}
              type="password"
              className="ltr-text"
              value={sudoPassword}
              autoComplete="off"
              onChange={(event) => setSudoPassword(event.target.value)}
            />
          )}
        </Field>

        <Checkbox
          checked={authorized}
          onChange={setAuthorized}
          label={t('servers.authorization')}
          help={t('servers.authorizationHelp')}
        />

        {error ? <Notice tone="danger">{error}</Notice> : null}

        <div className="form-actions">
          <Button variant="ghost" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          {/* Disabled rather than hidden: the customer can see exactly what is
              stopping them from continuing. */}
          <Button type="submit" busy={busy} disabled={!authorized}>
            {t('servers.add')}
          </Button>
        </div>
      </form>
    </Card>
  );
}
